import React, { useState } from 'react';
import NavigationBar from '../navbar/NavigationBar';
import Footer from '../navbar/Footer';
import { useNavigate } from 'react-router-dom'; 
import {
  Container,
  Typography,
  Grid,
  Card,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  styled,
  createTheme,
  ThemeProvider,
  Box,
  CardMedia,
  Button,
} from '@mui/material';
import moment from 'moment';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DownloadIcon from '@mui/icons-material/Download';

const bulletinConjoncturelData = [
  {
    title: "STATISTIQUES DES FLUX MIGRATOIRES ET TOURISTIQUES ÉDITION 2010",
    principalLink: "/documents//Annuaires/BCSF-Octobre-Décembre-2010.pdf",
    image: 'assets/annuaires/front.png',
    description: "L'analyse des flux migratoires et touristiques (Oct-Déc 2010) révèle que 75% des visiteurs non-résidents au Burundi proviennent du Rwanda, transitant principalement par les postes frontaliers de Kanyaru Haut (57,3% du trafic total) et Gasenyi (12,6%). L'aéroport de Bujumbura concentre 16,4% des arrivées, avec une répartition plus diversifiée (7% d'Européens dont 27% de Belges et 17% de Français).",
    date: "2010-12-30",
    publicationDate: "2010-12-30",
  },
];


function AnnuaireBulletinConjoncturel() {
  const [visibleCount, setVisibleCount] = useState(2);
  const [selectedYear, setSelectedYear] = useState('Tous');
  const [filteredBulletins, setFilteredBulletins] = useState(bulletinConjoncturelData);
  const navigate = useNavigate();

  const handleShowMore = () => {
    setVisibleCount((prevCount) => prevCount + 2);
  };

  const handleYearChange = (event) => {
    const year = event.target.value;
    setSelectedYear(year);
    if (year === 'Tous') {
      setFilteredBulletins(bulletinConjoncturelData);
    } else {
      const filtered = bulletinConjoncturelData.filter((bulletin) => {
        const bulletinYear = new Date(bulletin.date).getFullYear();
        return bulletinYear === parseInt(year);
      });
      setFilteredBulletins(filtered);
    }
  };

  const StyledSelect = styled(Select)(({ theme }) => ({
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.main,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.light,
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.dark,
    },
  }));

  const theme = createTheme({
    palette: {
      primary: {
        main: '#388E3C',
      },
      background: {
        default: '#f5f5f5',
      },
    },
    typography: {
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    },
  });

  return (
    <ThemeProvider theme={theme}>
      <div style={{ backgroundColor: theme.palette.background.default, paddingBottom: '32px' }}>
        <NavigationBar />

        <Container maxWidth="lg" style={{ paddingTop: '54px' }}>
          <Typography variant="h4" align="justify" gutterBottom style={{ color: theme.palette.primary.main }}>
            Bulletin Conjoncturel des Statistiques aux Frontières
          </Typography>
          <Divider style={{ margin: '16px 0' }} />
          <Typography variant="body1" align="justify" paragraph style={{ marginBottom: '24px' }}>
            Le Bulletin conjoncturel des statistiques aux frontières est un document essentiel qui présente une synthèse des analyses statistiques relatives aux mouvements de population aux frontières, en particulier aux flux des touristes internationaux venus et sortis du Burundi durant la période d’expérimentation de la nouvelle carte d’Entrée-Sortie. Depuis octobre 2010, avec le soutien du PNUD et de l’ISTEEBU, l’enregistrement des Entrées et Sorties aux postes frontières a permis une meilleure compréhension des flux migratoires. Une phase d’expérimentation de trois mois a été prévue pour tester la première version de cette carte.
          </Typography>

          <FormControl fullWidth variant="outlined" style={{ marginLeft: '0px', width: '300px' }}>
            <InputLabel id="year-select-label">Sélectionner par l'année de publication</InputLabel>
            <StyledSelect
              labelId="year-select-label"
              id="year-select"
              value={selectedYear}
              onChange={handleYearChange}
              label="Sélectionner par l'année de publication"
            >
              <MenuItem value="Tous">Toutes les statistiques</MenuItem>
              {[...new Set(bulletinConjoncturelData.map((bulletin) => new Date(bulletin.date).getFullYear()))]
                .sort((a, b) => b - a) 
                .map((year) => (
                  <MenuItem key={year} value={year.toString()}>
                    {year}
                  </MenuItem>
                ))}
            </StyledSelect>
          </FormControl>

          {/* Affichage des bulletins conjoncturels */}
          <Grid container spacing={1} style={{ marginTop: '10px' }}>
            {filteredBulletins.slice(0, visibleCount).map((bulletin, index) => (
              <Grid item xs={12} sm={12} key={index}>
                <Card elevation={3} style={{ borderRadius: '12px', overflow: 'hidden', display: 'flex', flexDirection: 'column', height: '100%' }}>
                  <Box sx={{ display: 'flex' }}>
                    <a
                      target="_blank"
                      href={bulletin.principalLink}
                      style={{ textDecoration: 'none', color: 'inherit' }}
                      rel="noopener noreferrer"
                    >
                      <CardMedia
                        component="img"
                        sx={{ width: 71, height: 120, m: 2 }}
                        image={bulletin.image}
                        alt={bulletin.title}
                      />
                    </a>
                    <Box sx={{ display: 'flex', flexDirection: 'column', pl: 2, pt: 2 }}>
                      <Typography variant="h6" component="div" style={{ fontWeight: 'bold', marginBottom: '10px' }}>
                        <a
                          href={bulletin.principalLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ textDecoration: 'none', color: theme.palette.primary.main }}
                        >
                          {<DownloadIcon />}
                          {bulletin.title}
                        </a>
                      </Typography>
                      <Typography variant="body2" component="div" style={{ color: '#555', marginBottom: '10px' }}>
                        {bulletin.description}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Publié en {moment(bulletin.publicationDate).format('MMMM YYYY')}
                      </Typography>
                    </Box>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>

          {visibleCount < filteredBulletins.length && (
            <Button
              onClick={handleShowMore}
              style={{
                marginTop: '20px',
                marginBottom: '20px',
                backgroundColor: theme.palette.primary.main,
                color: 'white',
              }}
            >
              Plus de Statistiques
            </Button>
          )}
                   <br />
          <Box display="flex" justifyContent="flex-start" alignItems="center">
            <Button
                 onClick={() => navigate('/annuaire')}
              style={{
                marginTop: '5px',
                backgroundColor: theme.palette.primary.main,
                color: 'white',
              }}
            >
              <ArrowBackIcon style={{ marginRight: '8px' }} />
              Retour à la page principale
            </Button>
          </Box>
        </Container>
        <br />
        <br />
        <Footer />
      </div>
    </ThemeProvider>
  );
}

export default AnnuaireBulletinConjoncturel;